import { Token } from '@kodiak-finance/sdk-core'
import ERC20_ABI from 'abis/erc20.json'
import { useAllTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { DEFAULT_DECIMALS, MAX_UINT256, UNLIMITED_CAP } from 'pages/KodiakFarmsDeploy/constants'
import { calculateMonthlyRewards } from 'pages/KodiakFarmsDeploy/helper'
import { useCallback, useEffect, useState } from 'react'
import { Abi, Address, formatUnits, PublicClient } from 'viem'

import { KODIAK_FARM_ABI } from '../../../components/KodiakFarms/KodiakFarm'

// Types
interface RewardToken {
  tokenAddress: string
  rewardRate: string
  rewardRateFormatted: string
  rewardManager: string
}

export interface FarmConfig {
  rewardTokens: RewardToken[]
  rewardsDuration: string
  stakingTokenCap: string
  lockTimeMax: string
  lockTimeMin: string
  lockMultiplierMax: number
  isLive: boolean
  lastUpdateTime: string
  stakingToken: string
}

interface UseFarmConfigReturn {
  farmConfig: FarmConfig | null
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

// Helper to format stakingTokenCap
const formatStakingCap = (cap: bigint, decimals: number = DEFAULT_DECIMALS): string => {
  const capStr = cap.toString()
  if (capStr === MAX_UINT256) {
    return UNLIMITED_CAP
  }
  return formatUnits(cap, decimals)
}

const getTokenInfo = async (
  tokenAddress: Address,
  wagmiPublicClient: PublicClient,
  chainId: number
): Promise<Token> => {
  try {
    // Fetch token details using multicall
    const results = await wagmiPublicClient.multicall({
      contracts: [
        {
          address: tokenAddress,
          abi: ERC20_ABI,
          functionName: 'decimals',
        },
        {
          address: tokenAddress,
          abi: ERC20_ABI,
          functionName: 'symbol',
        },
        {
          address: tokenAddress,
          abi: ERC20_ABI,
          functionName: 'name',
        },
      ],
    })

    if (results[0].status !== 'success' || results[1].status !== 'success' || results[2].status !== 'success') {
      throw new Error('Failed to fetch token info')
    }

    const decimals = results[0].result as number
    const symbol = results[1].result as string
    const name = results[2].result as string

    // Create and return a Token instance
    return new Token(
      chainId,
      tokenAddress,
      decimals,
      symbol,
      name,
      true // bypassChecksum
    )
  } catch (error) {
    console.warn(`Failed to get token info for ${tokenAddress}:`, error)

    // Return a fallback Token with default values
    return new Token(
      chainId,
      tokenAddress,
      DEFAULT_DECIMALS,
      'UNKNOWN',
      'Unknown Token',
      true // bypassChecksum
    )
  }
}

const useFarmConfig = (contractAddress: Address): UseFarmConfigReturn => {
  const { wagmiPublicClient, chainId } = useConnection()
  const [farmConfig, setFarmConfig] = useState<FarmConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const allTokens = useAllTokens()

  const fetchFarmConfig = useCallback(async () => {
    if (!wagmiPublicClient || !contractAddress) {
      setError(new Error('Wagmi client or contract address not available'))
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Fetch all farm config data using multicall
      const results = await wagmiPublicClient.multicall({
        contracts: [
          {
            address: contractAddress,
            abi: KODIAK_FARM_ABI,
            functionName: 'getAllRewardTokens',
          },
          {
            address: contractAddress,
            abi: KODIAK_FARM_ABI,
            functionName: 'getAllRewardRates',
          },
          {
            address: contractAddress,
            abi: KODIAK_FARM_ABI,
            functionName: 'rewardsDuration',
          },
          {
            address: contractAddress,
            abi: KODIAK_FARM_ABI,
            functionName: 'stakingTokenCap',
          },
          {
            address: contractAddress,
            abi: KODIAK_FARM_ABI,
            functionName: 'lock_time_for_max_multiplier',
          },
          {
            address: contractAddress,
            abi: KODIAK_FARM_ABI,
            functionName: 'lock_time_min',
          },
          {
            address: contractAddress,
            abi: KODIAK_FARM_ABI,
            functionName: 'lock_max_multiplier',
          },
          {
            address: contractAddress,
            abi: KODIAK_FARM_ABI,
            functionName: 'stakingToken',
          },
          {
            address: contractAddress,
            abi: KODIAK_FARM_ABI,
            functionName: 'lastUpdateTime',
          },
          {
            address: contractAddress,
            abi: KODIAK_FARM_ABI,
            functionName: 'stakingPaused',
          },
        ],
      })

      const rewardTokens = results[0].result as string[]
      const rewardRates = results[1].result as bigint[]
      const rewardsDuration = results[2].result as bigint
      const stakingTokenCap = results[3].result as bigint
      const lockTimeForMaxMultiplier = results[4].result as bigint
      const lockTimeMin = results[5].result as bigint
      const maxMultiplier = results[6].result as bigint
      const stakingToken = results[7].result as string
      const lastUpdateTime = results[8].result as bigint
      const stakingPaused = results[9].result as boolean

      // Get staking token info - first try from allTokens, then fallback to onchain
      let stakingTokenInfo: Token | undefined = allTokens[stakingToken.toLowerCase()]
      if (!stakingTokenInfo && chainId) {
        stakingTokenInfo = await getTokenInfo(stakingToken as Address, wagmiPublicClient, chainId)
      }
      const stakingTokenDecimals = stakingTokenInfo?.decimals || DEFAULT_DECIMALS

      // Get reward managers data using multicall
      const rewardManagerResults = await wagmiPublicClient.multicall({
        contracts: rewardTokens.map((tokenAddress: string) => ({
          address: contractAddress,
          abi: KODIAK_FARM_ABI as Abi,
          functionName: 'rewardManagers',
          args: [tokenAddress],
        })),
      })
      const rewardManagers = rewardManagerResults.map((result) => result.result)

      // Get token info for each reward token (first from allTokens, then fallback to onchain)
      const rewardTokenInfos = await Promise.all(
        rewardTokens.map(async (tokenAddress: string) => {
          const tokenFromList = allTokens[tokenAddress.toLowerCase()]
          if (tokenFromList) {
            return tokenFromList
          }
          if (chainId) {
            const tokenInfo = await getTokenInfo(tokenAddress as Address, wagmiPublicClient, chainId)
            return tokenInfo
          }
          return null
        })
      )

      // Format reward token data
      const rewardTokenData: RewardToken[] = rewardTokens.map((tokenAddress: string, index: number) => {
        const tokenInfo = rewardTokenInfos[index]
        const rate = rewardRates[index]
        const decimals = tokenInfo?.decimals ?? DEFAULT_DECIMALS
        return {
          tokenAddress,
          rewardRate: rate.toString(),
          rewardRateFormatted: calculateMonthlyRewards(formatUnits(rate, decimals), Number(rewardsDuration), decimals),
          rewardManager: rewardManagers[index] as string,
        }
      })

      const isLive = !stakingPaused && BigInt(lastUpdateTime) > 0n

      const newConfig: FarmConfig = {
        rewardTokens: rewardTokenData,
        rewardsDuration: rewardsDuration.toString(),
        stakingTokenCap: formatStakingCap(stakingTokenCap, stakingTokenDecimals),
        lockTimeMax: lockTimeForMaxMultiplier.toString(),
        lockTimeMin: lockTimeMin.toString(),
        lockMultiplierMax: Number(formatUnits(maxMultiplier, 18)),
        isLive,
        lastUpdateTime: lastUpdateTime.toString(),
        stakingToken,
      }
      setFarmConfig(newConfig)
      setError(null)
    } catch (err) {
      console.error('Error fetching farm config:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch farm config'))
      setFarmConfig(null)
    } finally {
      setLoading(false)
    }
  }, [contractAddress, wagmiPublicClient, chainId, allTokens])

  useEffect(() => {
    fetchFarmConfig()
  }, [fetchFarmConfig])

  return {
    farmConfig,
    loading,
    error,
    refetch: fetchFarmConfig,
  }
}

export default useFarmConfig
